import { useState, useEffect, useRef } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Plus, FileText, Edit2, Trash2, Smile, Meh, Frown, Star, Calendar, User, Stethoscope, Download, Sparkles, Loader2, CheckCircle2, Lock, Upload, Wand2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import TurndownService from "turndown";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { pdfToMarkdown } from "@/lib/pdfToMarkdown";
import { useConfirmation } from "@/components/ConfirmationProvider";

// pdfjs-dist requires a worker for parsing. Serve it from a CDN (matches installed version)
// so we don't need CRA/webpack asset copy plumbing.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const turndown = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced", bulletListMarker: "-" });

const MOOD_OPTIONS = [
  { value: "happy", label: "Happy", icon: Smile, color: "text-success" },
  { value: "neutral", label: "Neutral", icon: Meh, color: "text-golden-500" },
  { value: "difficult", label: "Difficult", icon: Frown, color: "text-coral-500" }
];

export default function SessionNotesPage() {
  const { user } = useAuth();
  const confirm = useConfirmation();
  const [notes, setNotes] = useState([]);
  const [children, setChildren] = useState([]);
  const [providers, setProviders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [extractOpen, setExtractOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [extractedActivities, setExtractedActivities] = useState([]);
  const [extractNoteCtx, setExtractNoteCtx] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfImportPrompt, setPdfImportPrompt] = useState(null); // { text, filename, pages }
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiDisabled, setAiDisabled] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const pdfInputRef = useRef(null);
  const isPremium = user?.subscription_tier === "premium";
  const [form, setForm] = useState({
    appointment_id: "",
    child_id: "",
    provider_id: "",
    content: "",
    mood: "neutral",
    progress_rating: 3
  });

  useEffect(() => {
    fetchData();
    // Fetch AI config once so we know whether to show the AI Cleanup button.
    // Silently ignore errors — button just stays hidden.
    if (user?.subscription_tier === "premium") {
      apiClient.get("/settings/ai")
        .then(r => {
          setAiConfigured(!!r.data?.configured);
          setAiDisabled(!!r.data?.ai_disabled);
        })
        .catch(() => {
          setAiConfigured(false);
          setAiDisabled(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [notesRes, childrenRes, providersRes, apptsRes] = await Promise.all([
        apiClient.get("/session-notes"),
        apiClient.get("/children"),
        apiClient.get("/providers"),
        apiClient.get("/appointments")
      ]);
      setNotes(notesRes.data);
      setChildren(childrenRes.data);
      setProviders(providersRes.data);
      setAppointments(apptsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNote) {
        await apiClient.put(`/session-notes/${editingNote.id}`, form);
        toast.success("Note updated!");
      } else {
        await apiClient.post("/session-notes", form);
        toast.success("Note created!");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete session note?", description: "This note will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/session-notes/${id}`);
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDownload = async (note) => {
    try {
      const res = await apiClient.get(`/export/session-notes/${note.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `session_note_${note.id.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Session note downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const handlePdfSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast.error("Please choose a PDF file");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("PDF too large (max 25MB)");
      return;
    }
    setPdfUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      // Rule-based structural conversion: headings, bold/italic, bullets, paragraph
      // breaks are inferred from pdfjs-dist positional + font metadata.
      const extracted = await pdfToMarkdown(arrayBuffer);
      // pageCount is useful for the toast; peek at pdfjs directly (cheap re-open)
      let pageCount = null;
      try {
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        pageCount = pdf.numPages;
      } catch { /* toast just won't include page count */ }

      if (!extracted) {
        toast.error("No text found in that PDF (it may be a scan or image-only)");
      } else if (!form.content?.trim()) {
        setForm(prev => ({ ...prev, content: extracted }));
        toast.success(pageCount ? `Imported ${pageCount} page${pageCount === 1 ? "" : "s"} from ${file.name}` : `Imported ${file.name}`);
      } else {
        setPdfImportPrompt({ text: extracted, filename: file.name, pages: pageCount });
      }
    } catch (err) {
      console.error("PDF parse error:", err);
      toast.error("Could not read that PDF. It may be encrypted or corrupted.");
    }
    setPdfUploading(false);
  };

  const applyPdfImport = (mode) => {
    if (!pdfImportPrompt) return;
    const incoming = pdfImportPrompt.text;
    setForm(prev => {
      if (mode === "replace") return { ...prev, content: incoming };
      const sep = prev.content?.endsWith("\n") ? "\n" : "\n\n";
      return { ...prev, content: (prev.content || "") + sep + incoming };
    });
    toast.success(`${mode === "replace" ? "Replaced" : "Appended"} content from ${pdfImportPrompt.filename}`);
    setPdfImportPrompt(null);
  };

  const handleAiCleanup = async () => {
    const current = (form.content || "").trim();
    if (current.length < 20) {
      toast.error("Add some content first — at least 20 characters to clean up.");
      return;
    }
    setCleaning(true);
    try {
      const res = await apiClient.post("/session-notes/clean-content", { content: form.content });
      const cleaned = res.data?.content;
      if (cleaned) {
        setForm(prev => ({ ...prev, content: cleaned }));
        toast.success(`Cleaned up with ${res.data?.provider || "AI"}`);
      } else {
        toast.error("AI returned empty content");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "AI cleanup failed";
      const code = err.response?.data?.code;
      if (code === "tier_premium_required") {
        toast.error("Upgrade to Premium to use AI features");
      } else if (detail.includes("AI not configured")) {
        toast.error("Add your AI key in Settings → AI (BYOK) first", { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
    setCleaning(false);
  };

  const handleEditorPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    const plain = e.clipboardData?.getData("text/plain");
    // Only intervene when the pasted content is HTML (rich text) — plain paste behaves normally
    if (!html || html.trim().length < 5) return;
    e.preventDefault();
    let md = "";
    try {
      md = turndown.turndown(html);
    } catch {
      md = plain || "";
    }
    if (!md) return;
    const el = e.target;
    // Insert at current cursor position in the textarea underlying MDEditor
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const before = form.content?.slice(0, start) || "";
      const after = form.content?.slice(end) || "";
      const next = before + md + after;
      setForm(prev => ({ ...prev, content: next }));
      // Move cursor after inserted text (deferred)
      requestAnimationFrame(() => {
        try {
          el.selectionStart = el.selectionEnd = start + md.length;
          el.focus();
        } catch { /* noop */ }
      });
    } else {
      setForm(prev => ({ ...prev, content: (prev.content || "") + md }));
    }
  };

  const openExtractDialog = async (note) => {
    setExtractNoteCtx(note);
    setExtractOpen(true);
    setExtracting(true);
    setExtractResult(null);
    setExtractedActivities([]);
    try {
      const r = await apiClient.post(`/session-notes/${note.id}/extract-activities`);
      setExtractResult(r.data);
      setExtractedActivities((r.data.activities || []).map(a => ({ ...a, _selected: true })));
    } catch (err) {
      const detail = err.response?.data?.detail || "Extraction failed";
      const code = err.response?.data?.code;
      if (code === "tier_premium_required") {
        toast.error("Upgrade to Premium to use AI features");
      } else if (detail.includes("AI not configured")) {
        toast.error("Add your AI key in Settings → AI (BYOK) first", { duration: 8000 });
      } else {
        toast.error(detail);
      }
      setExtractOpen(false);
    }
    setExtracting(false);
  };

  const saveSelectedActivities = async () => {
    const toSave = extractedActivities.filter(a => a._selected);
    if (!toSave.length || !extractNoteCtx) return;
    try {
      await Promise.all(toSave.map(a => apiClient.post("/activities", {
        child_id: extractNoteCtx.child_id,
        title: a.title,
        description: a.description,
        category: a.category || "therapy",
        priority: a.priority || "medium"
      })));
      toast.success(`Saved ${toSave.length} activit${toSave.length === 1 ? "y" : "ies"}`);
      setExtractOpen(false);
    } catch {
      toast.error("Failed to save activities");
    }
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setForm({
      appointment_id: note.appointment_id,
      child_id: note.child_id,
      provider_id: note.provider_id,
      content: note.content,
      mood: note.mood,
      progress_rating: note.progress_rating
    });
    setDialogOpen(true);
  };

  const openView = (note) => {
    setViewingNote(note);
    setViewOpen(true);
  };

  // Strip markdown syntax for a plain-text preview in list cards
  const previewText = (md, len = 140) => {
    if (!md) return "";
    const plain = md
      .replace(/```[\s\S]*?```/g, " ")           // code blocks
      .replace(/`[^`]*`/g, " ")                    // inline code
      .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")       // images
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")     // links -> text
      .replace(/^#{1,6}\s+/gm, "")                 // headings
      .replace(/\*\*([^*]+)\*\*/g, "$1")           // bold
      .replace(/\*([^*]+)\*/g, "$1")               // italic
      .replace(/^\s*[-*+]\s+/gm, "• ")             // bullets
      .replace(/^\s*\d+\.\s+/gm, "")               // ordered list markers
      .replace(/>\s?/g, "")                         // blockquotes
      .replace(/\s+/g, " ")
      .trim();
    return plain.length > len ? plain.slice(0, len).trimEnd() + "…" : plain;
  };

  const resetForm = () => {
    setForm({
      appointment_id: "",
      child_id: "",
      provider_id: "",
      content: "",
      mood: "neutral",
      progress_rating: 3
    });
    setEditingNote(null);
    setDialogOpen(false);
  };

  const getChildName = (id) => children.find(c => c.id === id)?.name || "Unknown";
  const getProviderName = (id) => providers.find(p => p.id === id)?.name || "Unknown";
  const getAppointmentTitle = (id) => appointments.find(a => a.id === id)?.title || "Session";

  const getMoodIcon = (mood) => {
    const option = MOOD_OPTIONS.find(m => m.value === mood);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`h-5 w-5 ${option.color}`} />;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="session-notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <FileText className="h-8 w-8" />
            Session Notes
          </h1>
          <p className="text-muted-foreground mt-1">Document therapy session observations</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-note-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Note
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingNote ? "Edit Session Note" : "Add Session Note"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Appointment (Optional)</Label>
                <Select value={form.appointment_id} onValueChange={(v) => {
                  const appt = appointments.find(a => a.id === v);
                  if (appt) {
                    setForm({ ...form, appointment_id: v, child_id: appt.child_id, provider_id: appt.provider_id });
                  } else {
                    setForm({ ...form, appointment_id: v });
                  }
                }}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Link to appointment" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointments.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.title} - {a.date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Child</Label>
                  <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })} required>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Therapist</Label>
                  <Select value={form.provider_id} onValueChange={(v) => setForm({ ...form, provider_id: v })} required>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select therapist" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label>Session Notes</Label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={handlePdfSelect}
                      data-testid="pdf-import-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => pdfInputRef.current?.click()}
                      disabled={pdfUploading || cleaning}
                      className="rounded-full h-8 text-xs"
                      data-testid="pdf-import-btn"
                    >
                      {pdfUploading ? (
                        <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Reading…</>
                      ) : (
                        <><Upload className="mr-1.5 h-3.5 w-3.5" /> Import PDF</>
                      )}
                    </Button>
                    {isPremium && aiConfigured && !aiDisabled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAiCleanup}
                        disabled={cleaning || pdfUploading}
                        className="rounded-full h-8 text-xs border-primary/40 text-primary hover:bg-primary/5"
                        title="Reformat this note with AI (uses your BYOK key)"
                        data-testid="ai-cleanup-btn"
                      >
                        {cleaning ? (
                          <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Cleaning…</>
                        ) : (
                          <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Ask AI to clean up</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <div data-color-mode="light" onPasteCapture={handleEditorPaste} data-testid="note-content-editor">
                  <MDEditor
                    value={form.content}
                    onChange={(v) => setForm({ ...form, content: v || "" })}
                    height={260}
                    preview="edit"
                    textareaProps={{
                      placeholder: "Document observations, progress, and activities… Supports **markdown**.",
                      required: true,
                      "data-testid": "note-content-input"
                    }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Tip: paste rich text and it will auto-convert to markdown. Import a PDF (e.g. clinical report) to pre-fill this note.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Child's Mood</Label>
                <div className="flex gap-2">
                  {MOOD_OPTIONS.map(option => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={form.mood === option.value ? "default" : "outline"}
                      onClick={() => setForm({ ...form, mood: option.value })}
                      className="flex-1 rounded-full"
                    >
                      <option.icon className={`mr-2 h-4 w-4 ${form.mood === option.value ? "" : option.color}`} />
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Progress Rating: {form.progress_rating}/5</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[form.progress_rating]}
                    onValueChange={(v) => setForm({ ...form, progress_rating: v[0] })}
                    min={1}
                    max={5}
                    step={1}
                    className="flex-1"
                  />
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`h-5 w-5 ${i <= form.progress_rating ? "text-golden-500 fill-golden-500" : "text-muted"}`} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-note-btn">
                  {editingNote ? "Update" : "Save"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Notes List */}
      {notes.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Session Notes Yet</h3>
            <p className="text-muted-foreground mb-6">Document your child's therapy sessions</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Add Note
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note, i) => (
            <Card 
              key={note.id} 
              onClick={() => openView(note)}
              className={`border-0 shadow-md hover:shadow-lg rounded-2xl card-hover animate-fadeIn stagger-${(i % 5) + 1} cursor-pointer transition-shadow`}
              data-testid={`note-card-${note.id}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-teal-700" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <h3 className="font-heading font-bold text-base truncate">
                        {getAppointmentTitle(note.appointment_id)}
                      </h3>
                      <Badge variant="secondary" className="rounded-full text-[10px] px-2 py-0">
                        <User className="h-3 w-3 mr-1" />
                        {getChildName(note.child_id)}
                      </Badge>
                      <Badge variant="secondary" className="rounded-full text-[10px] px-2 py-0">
                        <Stethoscope className="h-3 w-3 mr-1" />
                        {getProviderName(note.provider_id)}
                      </Badge>
                    </div>

                    <p
                      className="text-sm text-muted-foreground mb-2 line-clamp-2"
                      data-testid={`note-preview-${note.id}`}
                    >
                      {previewText(note.content) || <span className="italic">No content</span>}
                    </p>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        {getMoodIcon(note.mood)}
                        <span className="capitalize">{note.mood}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`h-3.5 w-3.5 ${i <= note.progress_rating ? "text-golden-500 fill-golden-500" : "text-muted"}`} />
                        ))}
                      </div>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(parseISO(note.created_at), "MMM d, yyyy")}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {!aiDisabled && (isPremium ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); openExtractDialog(note); }}
                        className="rounded-full h-8 w-8 text-primary"
                        title="Extract activities with AI"
                        data-testid={`extract-activities-${note.id}`}
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <RouterLink
                        to="/#pricing"
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
                        title="Extract activities with AI (Premium)"
                      >
                        <Lock className="h-3 w-3" />
                      </RouterLink>
                    ))}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDownload(note); }}
                      className="rounded-full h-8 w-8"
                      title="Download as CSV"
                      data-testid={`download-note-${note.id}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); openEdit(note); }}
                      className="rounded-full h-8 w-8"
                      title="Edit"
                      data-testid={`edit-note-${note.id}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                      className="rounded-full h-8 w-8 text-destructive hover:text-destructive"
                      title="Delete"
                      data-testid={`delete-note-${note.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI Extract Activities Dialog */}
      <Dialog open={extractOpen} onOpenChange={setExtractOpen}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[80vh] overflow-y-auto" data-testid="extract-activities-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Suggested Activities
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">Premium</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            {extracting ? (
              <div className="py-12 text-center">
                <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">Reading the session note and extracting follow-up activities…</p>
              </div>
            ) : extractedActivities.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground italic">No activities could be extracted. The note may be too brief.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Select the ones you want to add as activities for <span className="font-semibold">{children.find(c => c.id === extractNoteCtx?.child_id)?.name || "this child"}</span>:
                </p>
                <div className="space-y-2">
                  {extractedActivities.map((a, i) => (
                    <label
                      key={i}
                      className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${a._selected ? "border-primary bg-primary/5" : "border-border"}`}
                      data-testid={`extracted-activity-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={a._selected}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setExtractedActivities(prev => prev.map((x, idx) => idx === i ? { ...x, _selected: v } : x));
                        }}
                        className="mt-1 h-4 w-4 accent-primary"
                        data-testid={`extracted-activity-check-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-sm">{a.title}</h4>
                          {a.priority && <Badge variant="outline" className="rounded-full text-[10px]">{a.priority}</Badge>}
                          {a.category && <Badge variant="secondary" className="rounded-full text-[10px]">{a.category}</Badge>}
                        </div>
                        {a.description && <p className="text-xs text-muted-foreground leading-relaxed">{a.description}</p>}
                      </div>
                    </label>
                  ))}
                </div>
                {extractResult?.provider && (
                  <p className="text-[11px] text-muted-foreground">
                    Generated by {extractResult.provider} · {extractResult.model}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setExtractOpen(false)} className="flex-1 rounded-full">Cancel</Button>
            <Button
              onClick={saveSelectedActivities}
              disabled={extracting || extractedActivities.filter(a => a._selected).length === 0}
              className="flex-1 rounded-full"
              data-testid="save-extracted-activities-btn"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Save Selected ({extractedActivities.filter(a => a._selected).length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* View Note Detail Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="view-note-dialog">
          {viewingNote && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {getAppointmentTitle(viewingNote.appointment_id)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="rounded-full">
                    <User className="h-3 w-3 mr-1" />
                    {getChildName(viewingNote.child_id)}
                  </Badge>
                  <Badge variant="secondary" className="rounded-full">
                    <Stethoscope className="h-3 w-3 mr-1" />
                    {getProviderName(viewingNote.provider_id)}
                  </Badge>
                  <Badge variant="outline" className="rounded-full">
                    <Calendar className="h-3 w-3 mr-1" />
                    {format(parseISO(viewingNote.created_at), "MMM d, yyyy")}
                  </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-6 text-sm">
                  <div className="flex items-center gap-1.5">
                    {getMoodIcon(viewingNote.mood)}
                    <span className="capitalize">{viewingNote.mood}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">Progress</span>
                    <div className="flex">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`h-4 w-4 ${i <= viewingNote.progress_rating ? "text-golden-500 fill-golden-500" : "text-muted"}`} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <div
                    className="prose prose-sm max-w-none prose-p:my-2 prose-headings:mt-3 prose-headings:mb-1"
                    data-color-mode="light"
                    data-testid="view-note-content"
                  >
                    <MDEditor.Markdown
                      source={viewingNote.content || ""}
                      style={{ background: "transparent", color: "inherit", fontSize: "0.95rem" }}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
                <Button variant="outline" onClick={() => setViewOpen(false)} className="rounded-full" data-testid="view-note-close-btn">
                  Close
                </Button>
                <Button
                  onClick={() => { setViewOpen(false); openEdit(viewingNote); }}
                  className="rounded-full"
                  data-testid="view-note-edit-btn"
                >
                  <Edit2 className="mr-2 h-4 w-4" />
                  Edit Note
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Import Mode Prompt */}
      <Dialog open={!!pdfImportPrompt} onOpenChange={(o) => { if (!o) setPdfImportPrompt(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="pdf-import-mode-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Import PDF Content
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              Extracted <span className="font-semibold text-foreground">{pdfImportPrompt?.pages || "?"}</span> page{pdfImportPrompt?.pages === 1 ? "" : "s"} from <span className="font-semibold text-foreground">{pdfImportPrompt?.filename}</span>.
            </p>
            <p>Your editor already has content. What should we do?</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" onClick={() => setPdfImportPrompt(null)} className="rounded-full">Cancel</Button>
            <Button variant="outline" onClick={() => applyPdfImport("append")} className="rounded-full" data-testid="pdf-append-btn">
              Append to end
            </Button>
            <Button onClick={() => applyPdfImport("replace")} className="rounded-full" data-testid="pdf-replace-btn">
              Replace all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
